import type { HlsReadinessMetadata } from './HlsReadiness';

/**
 * Townhall Broadcast Destination, Session, and Output types.
 * Used by signaling server, web-next, and admin API routes.
 */

// --- Provider and Transport ---

export type TownhallBroadcastProvider = 'youtube' | 'twitch' | 'x' | 'custom_rtmp';

export type TownhallBroadcastTransport = 'rtmp';

// --- Session and Output Status ---

export type TownhallBroadcastSessionStatus =
  | 'starting'
  | 'live'
  | 'stopping'
  | 'ended'
  | 'failed';

export type TownhallBroadcastOutputStatus =
  | 'pending'
  | 'connecting'
  | 'live'
  | 'retrying'
  | 'failed'
  | 'stopped';

// --- Stream Endpoints (audience-facing) ---

export interface TownhallStreamEndpoints extends HlsReadinessMetadata {
  moqWt: string | null;
  /** AAC-transcoded MoQ path for Safari/iOS (Opus unsupported in WebCodecs). */
  moqWtAac: string | null;
  hls: string | null;
  live: boolean;
  /** Falls back to rooms.broadcastUrl when no active first-party session */
  legacyBroadcastUrl?: string | null;
}

// --- Destination Config (operator-facing) ---

export interface TownhallBroadcastDestinationConfig {
  id: string;
  roomId: string;
  provider: TownhallBroadcastProvider;
  transport: TownhallBroadcastTransport;
  label: string;
  enabled: boolean;
  isPrimaryAudienceFeed: boolean;
  ingestUrl: string | null;
  /** True if a stream key is configured, false otherwise. Raw key never exposed. */
  streamKeyConfigured: boolean;
  playbackUrl: string | null;
  embedUrl: string | null;
  providerMetadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// --- Session State (operator + audience API) ---

export interface TownhallBroadcastSessionState {
  id: string;
  roomId: string;
  status: TownhallBroadcastSessionStatus;
  startedByUserId: string | null;
  primaryPlaybackUrl: string | null;
  moqStreamName: string | null;
  hlsManifestPath: string | null;
  startedAt: string | null;
  endedAt: string | null;
  failureReason: string | null;
  outputs?: TownhallBroadcastOutputState[];
}

export interface TownhallBroadcastOutputState {
  id: string;
  destinationId: string;
  destinationLabel: string;
  provider: TownhallBroadcastProvider;
  status: TownhallBroadcastOutputStatus;
  lastError: string | null;
  connectedAt: string | null;
  disconnectedAt: string | null;
}

// --- Provider-specific ingest URL hints ---

export const BROADCAST_PROVIDER_INGEST_HINTS: Record<TownhallBroadcastProvider, string> = {
  youtube: 'rtmp://a.rtmp.youtube.com/live2',
  twitch: 'rtmp://live.twitch.tv/app',
  x: '',
  custom_rtmp: '',
};

export const BROADCAST_PROVIDER_LABELS: Record<TownhallBroadcastProvider, string> = {
  youtube: 'YouTube',
  twitch: 'Twitch',
  x: 'X',
  custom_rtmp: 'Custom RTMP',
};
